import { useState } from 'react';
import { MapPin, Calendar, Clock, User } from 'lucide-react';

const DEFAULT_PARENT = 'ABC';
const DEFAULT_DATE = '8月15日';
const DEFAULT_TIME = '11:00';
const DEFAULT_LOCATION = '荃灣青山道264-298號南豐中心19樓1922室';

function WhatsAppBubble({
  label,
  children,
  buttons,
}: {
  label: string;
  children: React.ReactNode;
  buttons?: { text: string; onClick?: () => void }[];
}) {
  return (
    <div className="space-y-3">
      <div className="text-xs text-gray-500 font-medium">{label}</div>
      <div className="inline-block max-w-[92%] sm:max-w-[80%]">
        <div className="bg-[#DCF8C6] text-gray-800 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm text-[15px] leading-relaxed whitespace-pre-wrap">
          {children}
          <div className="text-right text-[10px] text-gray-500 mt-1">10:08</div>
        </div>
        {buttons && buttons.length > 0 && (
          <div className="mt-2 space-y-2">
            {buttons.map((b, i) => (
              <button
                key={i}
                onClick={b.onClick}
                className="w-full bg-white border border-[#C2410C] text-[#C2410C] rounded-full py-2 text-sm font-medium hover:bg-orange-50 transition"
              >
                {b.text}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function InputRow({
  icon: Icon,
  label,
  value,
  onChange,
}: {
  icon: React.ElementType;
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex items-center gap-3">
      <Icon size={16} className="text-gray-400 flex-shrink-0" />
      <div className="flex-1">
        <label className="block text-xs text-gray-500 mb-1">{label}</label>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
        />
      </div>
    </div>
  );
}

export default function SenPlanTemplateDemo() {
  const [parentName, setParentName] = useState(DEFAULT_PARENT);
  const [sessionDate, setSessionDate] = useState(DEFAULT_DATE);
  const [sessionTime, setSessionTime] = useState(DEFAULT_TIME);
  const [location, setLocation] = useState(DEFAULT_LOCATION);

  const t2Body = `你好 ${parentName}，

期待這週六（${sessionDate}）上午 ${sessionTime} 在荃灣南豐中心見到你！

這幾天有家長問：「不懂電腦、沒做過生意能參加嗎？」如果你也有這個顧慮，請放心。我們主打「線上社區網店」，只需一部手機、在自己熟悉的屋苑用 WhatsApp 就能操作，完全沒有實體檔口租金壓力。

請按下方按鈕確認是否出席，以便我們安排座位。`;

  const t1Body = `你好 ${parentName}，

明天（${sessionDate}）上午 ${sessionTime} 就是「好餸·鄰里」簡介會，請記得準時出席。

📍 地點：${location}
⏰ 時間：${sessionDate} ${sessionTime}
📝 現場會有 QR code 海報，請用手機掃描完成簽到，即可取得當天簡介會資料。

如有任何更改，請盡快聯絡我們。
明天見！`;

  return (
    <div className="min-h-screen bg-[#E5DDD5] p-4 sm:p-6">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="bg-white rounded-2xl p-5 shadow-sm">
          <h1 className="text-xl font-bold text-gray-900">SEN 計劃 WhatsApp 模板預覽</h1>
          <p className="text-sm text-gray-600 mt-1">
            以 <span className="font-mono font-bold text-[#C2410C]">ABC</span> 作為示範家長，模擬 T-2 與 T-1 提醒訊息在 WhatsApp 的呈現效果。
          </p>
        </div>

        <div className="bg-white rounded-2xl p-5 shadow-sm space-y-4">
          <h2 className="font-bold text-gray-900">示範資料（可自行修改）</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <InputRow icon={User} label="家長名稱" value={parentName} onChange={setParentName} />
            <InputRow icon={Calendar} label="活動日期" value={sessionDate} onChange={setSessionDate} />
            <InputRow icon={Clock} label="活動時間" value={sessionTime} onChange={setSessionTime} />
            <InputRow icon={MapPin} label="活動地點（T-1 用）" value={location} onChange={setLocation} />
          </div>
        </div>

        <div className="bg-[#F0F2F5] rounded-2xl p-4 sm:p-6 shadow-sm space-y-8">
          <WhatsAppBubble
            label="T-2 提醒（活動前數天發送）— sen_plan_t2_reminder_v1"
            buttons={[
              { text: '準時出席' },
              { text: '未能出席' },
            ]}
          >
            {t2Body}
            {'\n\n'}好餸社企
          </WhatsAppBubble>

          <hr className="border-gray-300" />

          <WhatsAppBubble
            label="T-1 提醒（活動前一天發送）— sen_plan_t1_reminder_v1"
          >
            {t1Body}
            {'\n\n'}好餸社企
          </WhatsAppBubble>
        </div>

        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 text-sm text-orange-800">
          <p className="font-bold mb-1">檢閱重點</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>T-2 包含「準時出席 / 未能出席」兩個 Quick Reply 按鈕。</li>
            <li>T-1 為純文字提醒，不帶按鈕，重點在時間、地點與 QR code 簽到提示。</li>
            <li>變數 {'{{1}}'}、{'{{2}}'}、{'{{3}}'}、{'{{4}}'} 會於發送時由後台自動帶入。</li>
            <li>目前兩個模板在 Meta 後台狀態為 PENDING，審批通過後才能正式發送。</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
